/**
 * Emergency Recovery System for Dashboard
 * This module provides tools to handle and recover from crashes and excessive API calls
 */

// Store health metrics in session storage to persist across page refreshes
const METRICS_KEY = 'dashboard_health_metrics';
const CRASH_LIMIT = 3; // Number of crashes before enforcing safe mode

/**
 * Initialize the recovery system
 */
export function initializeRecovery() {
  // Track page load as recovery from potential crash
  const metrics = getMetrics();
  
  // Check if we've reloaded too many times in a short period (potential crash loop)
  const now = Date.now();
  const recentLoads = metrics.reloads.filter(time => now - time < 60000);
  
  if (recentLoads.length >= CRASH_LIMIT) {
    console.warn('🚨 [Recovery] Too many reloads detected, entering safe mode');
    enableSafeMode();
  }
  
  // Record this page load
  metrics.reloads.push(now);
  
  // Keep only recent reloads
  metrics.reloads = metrics.reloads.filter(time => now - time < 300000);
  
  // Update metrics
  saveMetrics(metrics);
  
  // Set up global error handler
  window.addEventListener('error', trackError);
  window.addEventListener('unhandledrejection', trackPromiseError);
  
  // Set up periodic check to disable safe mode after recovery
  setInterval(checkRecovery, 30000);
  
  // Export current mode for other components to check
  return {
    isSafeMode: isSafeModeEnabled()
  };
}

/**
 * Get current health metrics
 */
function getMetrics() {
  try {
    const data = sessionStorage.getItem(METRICS_KEY);
    return data ? JSON.parse(data) : {
      reloads: [],
      errors: [],
      apiFailures: 0,
      lastError: null
    };
  } catch (e) {
    // If storage is corrupted, reset metrics
    return { reloads: [], errors: [], apiFailures: 0, lastError: null };
  }
}

/**
 * Save updated metrics
 */
function saveMetrics(metrics) {
  try {
    sessionStorage.setItem(METRICS_KEY, JSON.stringify(metrics));
  } catch (e) {
    console.error('Failed to save metrics', e);
  }
}

/**
 * Track JavaScript errors
 */
function trackError(event) {
  const metrics = getMetrics();
  metrics.errors.push({
    time: Date.now(),
    message: event.message,
    source: event.filename
  });
  metrics.lastError = event.message;
  saveMetrics(metrics);
}

/**
 * Track unhandled promise rejections
 */
function trackPromiseError(event) {
  const metrics = getMetrics();
  metrics.errors.push({
    time: Date.now(),
    message: event.reason?.message || 'Unhandled Promise Rejection',
    source: 'promise'
  });
  metrics.lastError = event.reason?.message || 'Unhandled Promise Rejection';
  saveMetrics(metrics);
}

/**
 * Enable safe mode to limit dashboard functionality
 */
function enableSafeMode() {
  localStorage.setItem('dashboard_safe_mode', 'true');
  localStorage.setItem('dashboard_safe_mode_time', Date.now().toString());
}

/**
 * Disable safe mode
 */
function disableSafeMode() {
  localStorage.removeItem('dashboard_safe_mode');
  localStorage.removeItem('dashboard_safe_mode_time');
}

/**
 * Check if safe mode is currently enabled
 */
export function isSafeModeEnabled() {
  const safeMode = localStorage.getItem('dashboard_safe_mode') === 'true';
  
  if (safeMode) {
    // Check if safe mode has expired (30 minutes)
    const enabledTime = parseInt(localStorage.getItem('dashboard_safe_mode_time') || '0');
    if (Date.now() - enabledTime > 30 * 60 * 1000) {
      disableSafeMode();
      return false;
    }
    return true;
  }
  
  return false;
}

/**
 * Check if system has recovered and can disable safe mode
 */
function checkRecovery() {
  if (!isSafeModeEnabled()) return;
  
  const metrics = getMetrics();
  const now = Date.now();
  
  // If no errors in last 5 minutes and fewer than 2 reloads, consider recovered
  const recentErrors = metrics.errors.filter(err => now - err.time < 300000);
  const recentReloads = metrics.reloads.filter(time => now - time < 300000);
  
  if (recentErrors.length === 0 && recentReloads.length < 2) {
    console.log('✅ [Recovery] System appears stable, disabling safe mode');
    disableSafeMode();
  }
}

/**
 * Track API failure
 */
export function trackApiFailure() {
  const metrics = getMetrics();
  metrics.apiFailures++;
  saveMetrics(metrics);
  
  // If too many API failures, enable safe mode
  if (metrics.apiFailures > 15) {
    console.warn('🚨 [Recovery] Too many API failures, entering safe mode');
    enableSafeMode();
    
    // Reset failure count after enabling safe mode
    metrics.apiFailures = 0;
    saveMetrics(metrics);
  }
}

export default {
  initializeRecovery,
  isSafeModeEnabled,
  trackApiFailure
};
